"use client";

import React from "react";
import { MapPin, Phone, Mail, Facebook, Instagram, Linkedin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        {[1, 2, 3, 4].map((_, i) => <li key={i} data-index={i}>
            <div className="h-4 w-24 bg-muted-foreground/20 rounded-md" />
          </li>)}
      </ul>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="space-y-3">
        <li data-index={0}>
          <Link to="/HomePage" className="text-muted-foreground hover:text-primary transition-colors text-sm font-default">Home</Link>
        </li>
        <li data-index={1}>
          <Link to="/WeddingsGalasPage" className="text-muted-foreground hover:text-primary transition-colors text-sm font-default">Weddings & Galas</Link>
        </li>
        <li data-index={2}>
          <Link to="/CorporateLivePage" className="text-muted-foreground hover:text-primary transition-colors text-sm font-default">Corporate & Live</Link>
        </li>
        <li data-index={3}>
          <Link to="/PulseFXPage" className="text-muted-foreground hover:text-primary transition-colors text-sm font-default">PulseFX</Link>
        </li>
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-muted-foreground hover:text-primary transition-colors text-sm font-default">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function Footer() {
  const services = ["Live Event Production", "Wedding AV Solutions", "PulseFX Special Effects", "LED Screen Hire", "Professional Lighting"];
  const servicePathMap: Record<string, string> = {
    "Live Event Production": "/CorporateLivePage",
    "Wedding AV Solutions": "/WeddingsGalasPage",
    "PulseFX Special Effects": "/PulseFXPage",
    "LED Screen Hire": "/EquipmentHirePage",
    "Professional Lighting": "#"
  };
  return <section data-section-id="259873" id="footer" className="bg-background border-t border-border pt-16 pb-8">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 mb-12">
          
          {}
          <div className="flex flex-col space-y-4">
            <Link to="/HomePage" className="inline-block mb-2">
              <WvcLogo className="h-8 w-auto text-foreground" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs font-default">UK-based audio visual production specialists with over 20 years of experience. Weddings, live events, conferences and special effects — delivered with precision and passion.</p>
          </div>

          {}
          <div className="flex flex-col space-y-4">
            <h3 className="font-default font-bold text-foreground text-lg tracking-tight">Quick Links</h3>
            <MenuProvider menu_id="47">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex flex-col space-y-4">
            <h3 className="font-default font-bold text-foreground text-lg tracking-tight">Our Expertise</h3>
            <ul className="space-y-3">
              {services.map((service, i) => <li key={i} data-index={i}>
                  <span className="text-muted-foreground text-sm font-default">
                    {service}
                  </span>
                </li>)}
            </ul>
          </div>

          {}
          <div className="flex flex-col space-y-4">
            <h3 className="font-default font-bold text-foreground text-lg tracking-tight">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3 text-sm text-muted-foreground font-default">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                <span>412 Katherine Rd, London E7 8NP</span>
              </li>
              <li className="flex items-center space-x-3 text-sm text-muted-foreground font-default">
                <Phone className="w-4 h-4 shrink-0 text-primary" />
                <Link to="tel:+442012345678" className="hover:text-primary transition-colors">+44 (0) 7903 733068</Link>
              </li>
              <li className="flex items-center space-x-3 text-sm text-muted-foreground font-default">
                <Mail className="w-4 h-4 shrink-0 text-primary" />
                <Link to="mailto:hello@pulseav.co.uk" className="hover:text-primary transition-colors">info@pulseav.co.uk</Link>
              </li>
            </ul>

            <div className="flex items-center space-x-4 mt-4 pt-2">
              <Link to="#" className="w-8 h-8 rounded-md bg-card flex items-center justify-center text-muted-foreground hover:text-primary hover:shadow-lg hover:shadow-primary/20 transition-all border border-border">
                <Facebook className="w-4 h-4" />
                <span className="sr-only">Facebook</span>
              </Link>
              <Link to="https://www.instagram.com/pulse_av_/" className="w-8 h-8 rounded-md bg-card flex items-center justify-center text-muted-foreground hover:text-secondary hover:shadow-lg hover:shadow-secondary/20 transition-all border border-border">
                <Instagram className="w-4 h-4" />
                <span className="sr-only">Instagram</span>
              </Link>
              <Link to="https://www.linkedin.com/company/pulse-av-productions/?viewAsMember=true" className="w-8 h-8 rounded-md bg-card flex items-center justify-center text-muted-foreground hover:text-chart-3 hover:shadow-lg hover:shadow-chart-3/20 transition-all border border-border">
                <Linkedin className="w-4 h-4" />
                <span className="sr-only">LinkedIn</span>
              </Link>
            </div>
          </div>

        </div>

        <Separator className="bg-border" />

        {}
        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground font-mono">
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4 text-center md:text-left">
            <span>© 2026 Pulse AV. All rights reserved.</span>
          </div>
          
          <div className="flex items-center gap-4">
            
            
          </div>
        </div>
      </div>
    </section>;
}